'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ContactStep from './ContactStep';
import VehicleStep from './VehicleStep';
import PaymentPreferenceSection from './PaymentPreferenceSection';
import Button from '@/components/Button';
import ThankYouPopup from './thank-you/ThankYouPopup';
// If you want interval auto-save for other steps, you can import useAutoSave
// import useAutoSave from './useAutoSave';

// Helper to convert string to title case
const toTitleCase = (str) =>
  str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );

// Helper function to classify the vehicle
const classifyVehicle = async ({ year, make, model, trim }) => {
  const payload = { year, make, model, trim };
  const res = await fetch('/api/vehicle-classifier', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (json.error) {
    throw new Error(json.error);
  }
  return json;
};

// Validation schema for step 3 (dynamically created)
const createValidationSchema = (requestType) => {
  let schema = Yup.object({
    timelinePreference: Yup.string().required(
      'Timeline Preference is required',
    ),
    consent: Yup.boolean().oneOf([true], 'Consent is required'),
  });
  if (requestType === 'Appointment Request') {
    schema = schema.shape({
      paymentOption: Yup.string().required('Please select a payment option'),
    });
  }
  return schema;
};

const stepSchemas = [
  // Step 1: Contact Information & Services
  Yup.object({
    requestType: Yup.string()
      .oneOf(
        ['Appointment Request', 'Estimate Request'],
        'Invalid Request Type',
      )
      .required('Request Type is required'),
    firstName: Yup.string().required('Your First Name is required'),
    lastName: Yup.string().required('Your Last Name is required'),
    phone: Yup.string().required('Phone is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    mainInterest: Yup.array()
      .min(1, 'Select at least one interest option')
      .required('Interest is required'),
  }),
  // Step 2: Vehicle Information & Enhancements
  Yup.object({
    auto_year: Yup.string().required('Vehicle Year is required'),
    auto_make: Yup.string().required('Vehicle Make is required'),
    auto_model: Yup.string().required('Vehicle Model is required'),
  }),
];

const timelineOptions = [
  'As Soon As Possible - I am ready to protect my vehicle',
  'Within the next 14 days - I need time to plan',
  'Flexible - I am not in a hurry; my schedule is flexible',
];

export default function EstimateForm({
  id,
  service,
  interestOptions = ['General Estimate'],
  enhancementOptions = ['Basic Package', 'Premium Package'],
  serviceCategory,
  preselectedService,
  category,
}) {
  const finalInterestOptions =
    serviceCategory?.services?.length > 0
      ? serviceCategory.services
      : category?.services?.length > 0
        ? category.services
        : interestOptions;

  const finalEnhancementOptions =
    serviceCategory?.serviceEnhancements?.length > 0
      ? serviceCategory.serviceEnhancements
      : category?.serviceEnhancements?.length > 0
        ? category.serviceEnhancements
        : enhancementOptions;

  const formTitle = service || 'Services';
  const [step, setStep] = useState(0);
  const [showThankYou, setShowThankYou] = useState(false);
  const [thankYouParams, setThankYouParams] = useState({});
  const [sourceUrl, setSourceUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formikRef = useRef();

  useEffect(() => {
    setSourceUrl(window.location.href);
  }, []);

  // Uncomment the following if you want interval auto-save for the whole form:
  // useAutoSave(formikRef, 30000);

  const initialValues = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    interest: formTitle,
    mainInterest: preselectedService || [],
    consent: false,
    auto_year: '',
    auto_make: '',
    auto_model: '',
    trim: '',
    enhancements: [],
    notes: '',
    additionalInfo: '',
    timelinePreference: '',
    requestType: 'Appointment Request',
    paymentOption: '',
    source: sourceUrl,
  };

  const validateCurrentStep = (values) => {
    try {
      let schema =
        step < 2
          ? stepSchemas[step]
          : createValidationSchema(values.requestType);
      schema.validateSync(values, { abortEarly: false });
      return {};
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      return errors;
    }
  };

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);

  // Function to auto-save step one; called when clicking Next on step one.
  const autoSaveStepOne = async (values) => {
    try {
      await fetch('/api/auto-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      console.log('Auto-saved step one data');
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    let vehicleClassification;
    try {
      vehicleClassification = await classifyVehicle({
        year: values.auto_year,
        make: values.auto_make,
        model: values.auto_model,
        trim: values.trim,
      });
    } catch (error) {
      console.error('Error classifying vehicle:', error);
      vehicleClassification = { category: 'Unknown' };
    }

    let combinedNotes = values.notes || '';
    if (values.requestType) {
      combinedNotes += `\nRequest Type: ${values.requestType}`;
    }
    if (values.paymentOption) {
      combinedNotes += `\nPayment Option: ${values.paymentOption}`;
    }
    if (values.timelinePreference) {
      combinedNotes += `\nTimeline Preference: ${values.timelinePreference}`;
    }
    if (vehicleClassification && vehicleClassification.category) {
      combinedNotes += `\nVehicle Size: ${vehicleClassification.category}`;
    }
    if (values.mainInterest.length > 0) {
      combinedNotes += `\nInterest: ${values.mainInterest.join(', ')}`;
    }
    if (values.enhancements.length > 0) {
      combinedNotes += `\nEnhancements: ${values.enhancements.join(', ')}`;
    }
    if (values.additionalInfo) {
      combinedNotes += `\nAdditional Info: ${values.additionalInfo}`;
    }
    if (values.consent) {
      combinedNotes += `\nConsent: ${new Date().toLocaleString()}`;
    }
    if (values.source) {
      combinedNotes += `\nWebsite Source: ${values.source}`;
    }

    const payload = {
      email: values.email,
      phone: values.phone,
      contactName: `${values.firstName} ${values.lastName}`,
      firstName: values.firstName,
      lastName: values.lastName,
      tags: ['lead'],
      source: formTitle,
      request_type: values.requestType,
      interested_in: values.mainInterest[0],
      enhancements: values.enhancements.join(', '),
      payment_options: values.paymentOption,
      notes: combinedNotes,
      timeline: values.timelinePreference,
      website_source: values.source,
      vehicle_information: {
        vehicle_year: values.auto_year,
        vehicle_make: toTitleCase(values.auto_make),
        vehicle_model: toTitleCase(values.auto_model),
      },
    };

    try {
      const res = await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      console.log('Response data:', data);
      if (window.gtag) {
        window.gtag('event', 'form_submission', {
          event_category: 'Booking',
          event_label: 'Estimate Form Submitted',
        });
      }
      setThankYouParams(payload);
      setShowThankYou(true);
    } catch (error) {
      console.error('Error during submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={id}>
      <div className="flex flex-col items-center justify-center">
        <div className="tablet:p-12 w-full max-w-xl rounded-lg border border-gray-200 bg-white p-6 shadow-xl">
          <h1 className="mb-8 text-center text-3xl font-semibold text-gray-800 uppercase">
            {formTitle} Request
          </h1>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validate={validateCurrentStep}
            onSubmit={onSubmit}
            enableReinitialize={true}
            validateOnMount={true}
          >
            {({ values, validateForm, isValid }) => (
              <Form>
                {step === 0 && (
                  <ContactStep finalInterestOptions={finalInterestOptions} />
                )}
                {step === 1 && (
                  <VehicleStep
                    formTitle={formTitle}
                    finalEnhancementOptions={finalEnhancementOptions}
                  />
                )}
                {step === 2 && (
                  <div className="space-y-6">
                    {values.requestType === 'Appointment Request' && (
                      <PaymentPreferenceSection />
                    )}
                    <div>
                      <label
                        htmlFor="additionalInfo"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Additional Information
                      </label>
                      <Field
                        as="textarea"
                        id="additionalInfo"
                        name="additionalInfo"
                        placeholder="Any additional details..."
                        rows="4"
                        className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2"
                      />
                      <ErrorMessage
                        name="additionalInfo"
                        component="div"
                        className="mt-1 text-xs text-red-500"
                      />
                    </div>
                    <div className="mt-6">
                      <label
                        htmlFor="timelinePreference"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Timeline Preference{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <Field
                        as="select"
                        name="timelinePreference"
                        id="timelinePreference"
                        className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2"
                      >
                        <option value="">Select a preference</option>
                        {timelineOptions.map((option, i) => (
                          <option key={i} value={option}>
                            {option}
                          </option>
                        ))}
                      </Field>
                    </div>
                    <div className="mt-4 flex items-start">
                      <Field
                        type="checkbox"
                        name="consent"
                        className="mt-1 mr-3"
                      />
                      <label
                        htmlFor="consent"
                        className="text-sm text-gray-700"
                      >
                        By submitting this form, I consent to receive SMS
                        messages and/or emails from this company. To
                        unsubscribe, follow the instructions provided in our
                        communications. Msg & data rates may apply for SMS. Your
                        information is secure and will not be sold to third
                        parties. <span className="text-red-500">*</span>
                      </label>
                    </div>
                  </div>
                )}
                <div className="mt-8 flex justify-between">
                  {step > 0 && (
                    <Button type="button" onClick={prevStep} variant="grey">
                      Back
                    </Button>
                  )}
                  {step < 2 && (
                    <Button
                      type="button"
                      onClick={async () => {
                        const errs = await validateForm();
                        if (Object.keys(errs).length === 0) {
                          if (step === 0) {
                            // Auto-save step one before moving to step two.
                            const currentValues = formikRef.current?.values;
                            try {
                              await fetch('/api/auto-save', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(currentValues),
                              });
                              console.log('Auto-saved step one data');
                            } catch (error) {
                              console.error('Auto-save error:', error);
                            }
                          }
                          nextStep();
                        }
                      }}
                      disabled={!isValid}
                      variant="primary"
                      className={`${!isValid ? 'cursor-not-allowed opacity-50' : 'opacity-100'} ml-auto`}
                    >
                      Next
                    </Button>
                  )}
                  {step === 2 && (
                    <button
                      type="submit"
                      className="bg-primary border-primary text-mad-white hover:bg-primary/90 flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold uppercase shadow-md transition"
                    >
                      Submit
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {isSubmitting && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-gray-800">
          <div className="flex flex-col items-center rounded bg-white p-6 shadow-md">
            <div className="loader mb-4">
              <svg
                className="h-8 w-8 animate-spin text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                ></path>
              </svg>
            </div>
            <p>Submitting your info...</p>
          </div>
        </div>
      )}
      {showThankYou && (
        <ThankYouPopup
          searchParams={thankYouParams}
          onClose={() => setShowThankYou(false)}
        />
      )}
    </section>
  );
}
